/** @module ProductSpec/SizeChart  */
define([
	"csi/_base/underscore",
	"csi/session",
	"csi/page",
	"csi/reflection",
	"csi/util",
	"csi/view",
	"csi/xml",
	"src/c8-platform/service/aggregateStoreService",
], function(_, session, page, reflection, util, csiView, xml, {clearAggregateStoreResponsesByNodeType}) {

	var _settingsNode = reflection.getObject("centric://REFLECTION/INSTANCE/ProductSpecAdmin/null"),
		_roundToDenominator = xml.enumGetText(_settingsNode.SCRoundToFraction).split("/")[1],

		DEFAULT = -999.0,
		UNIT_FRACTION = "Size Chart Units:IN - Fraction",
		UNIT_CM = "Size Chart Units:CM",
		UNIT_MM = "Size Chart Units:MM",
		INC = "Increments",
		PWS = "PatternWithShrinkage",

		// indicates which vector(s) store values off base size as increment values rather than absolute
		VECTOR_STORES_INCREMENTS = {
			Increments: 1
		},

		VECTOR_REVISED = {
			Increments: "Revised",
			Pattern: "RevisedPattern",
			PatternWithShrinkage: "RevisedPatternWithShrinkage"
		},

		_isNotInitialized = function(/*Number*/x) {
			// summary: return true if given value is not initialized
			return x == null || x == DEFAULT || x == -DEFAULT;
		},

		_sumDoubleValues = function(/*Number*/x1, /*Number*/x2) {
			// summary: calculate the sum of two size chart values; it is required since the initial value of the double
			// vector of SizeChartDimension Increments / Pattern / PatternWithShrinkage are not zero
			var b1 = _isNotInitialized(x1);
			var b2 = _isNotInitialized(x2);

			if (b1 && b2)
				return DEFAULT;
			else if (b1)
				return x2;
			else if (b2)
				return x1;
			else
				return x1 + x2;
		},

		_getVectorValues = function(/*Context*/ctx, /*String*/vectorAttrId, /*Node*/dim, /*Double[]*/value) {
			// summary: create object to store dimension data with the following properties:
			// 	{
			// 		abs: Number[],
			// 		absIgnoreBlank: Number[],
			//		inc: Number[],
			//		str: Number,
			//		tol: Number,
			//		url: String - SizeChartDimension URL
			//	}
			var bIncrements = VECTOR_STORES_INCREMENTS[vectorAttrId];
			var cache = ctx.cache;
			var ranges = ctx.view.getSubSizeRangeData(dim);
			var n = value.length;

			var obj = {
				str: dim.Stretch + 1,
				tol: dim.Tolerance,
				url: dim.$URL
			};

			// incorporate revised values from review based on view state
			var rev;

			if (ctx.view._bReviewValues) {
				var idRevised = VECTOR_REVISED[vectorAttrId];

				_.each(cache.getNodes(dim.ReviewData), function(rd) {
					if (rd[idRevised] != DEFAULT) {
						var rs = cache.getNode(rd.ReviewSize);
						if (ranges.isBaseIndex(rs.SizeIndex)) {
							value[rs.SizeIndex] = rd[idRevised];
						} else {
							// store diff from target to be applied later
							rev = rev || {};
							rev[rs.SizeIndex] = rd[idRevised];
						}
					}
				});
			}

			var scRevData = ctx.view.getSizeChartRevisionData(dim);
			var isSizeChart2Dim = scRevData && scRevData.isTwoDim();
			var displayTwoDim = isSizeChart2Dim && ctx.view.displayTwoDimension(dim);

			var abs = obj.abs = [].concat(value);
			var inc = obj.inc = bIncrements ? [].concat(value) : new Array(n);

			// (Would be) The absolute mode vector if we treat all the blanks as zero
			var absIgnoreBlank = obj.absIgnoreBlank = [].concat(value);

			// if values off base size are stored as increments rather than absolute values, then convert now to absolute
			// FIX: this is necessary because we store Increments and Pattern(WithShrinkage) vectors differently
			if (bIncrements) {
				if (isSizeChart2Dim) {
					// When size chart has 2-d sizes
					var incrementToAbsoluteAndIgnoreBlank = scRevData.incrementToAbsoluteAndIgnoreBlank(inc, displayTwoDim);
					obj.abs = incrementToAbsoluteAndIgnoreBlank.abs;
					obj.absIgnoreBlank = incrementToAbsoluteAndIgnoreBlank.absIgnoreBlank;
				} else {
					ranges.iterate(null,
						function(x) {
							absIgnoreBlank[x] = _isNotInitialized(inc[x]) ? 0.0 : inc[x];
							abs[x] = inc[x];
						},
						function(x) {
							absIgnoreBlank[x] = _sumDoubleValues(absIgnoreBlank[x + 1], inc[x]);
							if (!_isNotInitialized(inc[x]))
								abs[x] = absIgnoreBlank[x];
						},
						function(x) {
							absIgnoreBlank[x] = _sumDoubleValues(absIgnoreBlank[x - 1], inc[x]);
							if (!_isNotInitialized(inc[x]))
								abs[x] = absIgnoreBlank[x];
						}
					);
				}
			}

			// reset increments for all but base size when blank dimension to show correct revised values;
			// the pre-existing increments are still stored but not used, and they should not factor in to revised calculation
			if (ctx.view._bReviewValues && dim.Blanks) {
				var f = function(x) {abs[x] = 0.0;};
				ranges.iterate(null, null, f, f);
			}

			// calculate increments from absolute values
			if (!bIncrements) {
				if (isSizeChart2Dim) {
					// When size chart has 2-d sizes
					obj.inc = scRevData.absoluteToIncrement(abs, displayTwoDim);
				} else {
					ranges.iterate(null,
						function(x) {
							absIgnoreBlank[x] = _isNotInitialized(abs[x]) ? 0.0 : abs[x];
							inc[x] = abs[x];
						},
						function(x) {
							if (!_isNotInitialized(abs[x])) {
								absIgnoreBlank[x] = abs[x];
								inc[x] = _sumDoubleValues(absIgnoreBlank[x], -absIgnoreBlank[x + 1]);
							} else {
								absIgnoreBlank[x] = absIgnoreBlank[x + 1];
							}
						},
						function(x) {
							if (!_isNotInitialized(abs[x])) {
								absIgnoreBlank[x] = abs[x];
								inc[x] = _sumDoubleValues(absIgnoreBlank[x], -absIgnoreBlank[x - 1]);
							} else {
								absIgnoreBlank[x] = absIgnoreBlank[x - 1];
							}
						}
					);
				}
			}

			// overwrite absolute values off of base size with revised values from review
			if (rev) {
				_.each(rev, function(v, k) {
					var base = ranges.getBaseIndex(k);
					var min = ranges.getMinIndex(k);
					var max = ranges.getMaxIndex(k);
					var diff = v - abs[k];

					abs[k] = v;
					inc[k] += diff;

					if ((k > min) && (k < base)) {
						inc[k - 1] -= diff;
					} else if (k + 1 <= max) {
						inc[k + 1] -= diff;
					}
				});
			}

			return obj;
		},

		_refreshDimensions = function(/*Controller*/ac) {
			// summary: refresh all dimension views, making sure postAction is called afterwards to update toolbar
			_.each(csiView.getAllViews("Dimensions"), function(view) {
				view.when(view.refreshWidget(), function() {
					view.postAction();
				});
			});
		};

	return {
		bPct: _settingsNode.SCFormatToleranceDelta.indexOf("%") >= 0,
		bSuggestRevised: _settingsNode.SCEvaluateSuggestRevised,
		formatAbs: _settingsNode.SCFormatAbsolute,
		formatInc: _settingsNode.SCFormatIncrement,
		formatTol: _settingsNode.SCFormatTolerance,
		formatTolDelta: _settingsNode.SCFormatToleranceDelta,
		gradingDefaultDisplay: _settingsNode.SCGradingDefaultDisplay,
		compareDefault: _settingsNode.SCCompareDefault,
		valueUnits: _settingsNode.SCValueUnits,

		isNotInitialized: _isNotInitialized,
		sumDoubleValues: _sumDoubleValues,

		getDimensionValues: function(/*Context*/ctx, /*String*/vectorAttrId, /*Boolean*/bFindComparisonValues) {
			// summary: return object with dimension values for given context
			var dim = ctx.getNode();
			var obj = ctx.bComparisonValues ? ctx.scdCompare : ctx.scdCache;

			obj = obj && obj[vectorAttrId];

			// store dimension values in context if not present or context has switched - same applies to conversion factor
			if (!obj || (obj.url != dim.$URL)) {
				var value = dim[vectorAttrId];

				// handle Pattern and PatternWithShrinkage
				if (vectorAttrId != INC) {
					if (!value.length) {
						// if value not filled in, then create array same length as Increments that is set to all defaults
						value = _.map(dim.Increments, function(x) {return DEFAULT;});
					}

					if (vectorAttrId == PWS) {
						// initialize PatternWithShrinkage if not already set
						var pattern = dim.Pattern;
						if (pattern.length) {
							var shrinkageFactor = 1 + dim.Shrinkage;

							value = _.map(value, function(x, i) {
								if (!_isNotInitialized(x)) {
									return x;
								}

								return _isNotInitialized(pattern[i]) ? DEFAULT : (pattern[i] * shrinkageFactor);
							});
						}
					}
				}

				ctx.scdCache = ctx.scdCache || {};
				ctx.scdCache[vectorAttrId] = obj = _getVectorValues(ctx, vectorAttrId, dim, value);

				// cache comparison values if so desired
				var compareValue = bFindComparisonValues && ctx.view.getComparableIncrements(dim);
				if (compareValue) {
					// for "Compare Increment", fill in base size values over existing increments
					if (ctx.view.bCompareIncrements) {
						ctx.view.getSubSizeRangeData(dim).iterate(null, function(x) {
							compareValue[x] = value[x];
						});
					}

					ctx.scdCompare = ctx.scdCompare || {};
					ctx.scdCompare[vectorAttrId] = _getVectorValues(ctx, vectorAttrId, dim, compareValue);
					ctx.view.setDimensionDifferences(dim, ctx.scdCache[vectorAttrId].inc, ctx.scdCompare[vectorAttrId].inc);
				}
			}

			return obj;
		},

		getSizeChartDimension: function(/*Context*/ctx) {
			// summary: return size chart dimension for context
			return ctx.getNodeOfType("SizeChartDimension");
		},

		isFractionUnit: function(valueUnits) {
			return valueUnits == UNIT_FRACTION;
		},

		onActionDimensionStretch: function(ac) {
			if ((ac.newValue >= 1.0) || (ac.newValue <= -1.0)) {
				// do not allow stretch to be 1.0 or more
				ac.m_writer.changeNode(ac.m_urlNode).changeAttribute("Stretch", xml.ATTR_DOUBLE, ac.oldValue);
			} else {
				// update base absolute value and all increments
				var dScale = (1.0 + ac.newValue) / (1.0 + ac.oldValue);
				var value = ac.getNode().Increments;
				var n = value.length;

				for (var i = 0; i < n; ++i) {
					value[i] = util.truncate(value[i] * dScale, 0.001);
				}

				ac.m_writer.changeNode(ac.m_urlNode).changeAttribute("Increments", xml.ATTR_DOUBLEVECTOR, value);
				ac.bRefreshNode = true;
			}
		},

		postActionRefreshDimensions: function(ac) {
			// summary: refresh the Dimensions view without need to query
			_refreshDimensions(ac);
		},

		/**
		 * Refresh any Dimensions views and clear aggregate store caches for ProductSize
		 * @param {Controller} ac
		 */
		postActionSizes: function(ac) {
			clearAggregateStoreResponsesByNodeType("ProductSize");
			_refreshDimensions(ac);
		},

		postActionSizeRange: function(ac) {
			// summary: refresh the Dimensions view
			ac.widget.refreshHeadingStore(ac.widget.defView.findHeading("SizeRange", false));
			this.postActionSizes(ac);
		},

		/**
		 * Action Change Size Chart Product Group / Increment
		 * For composite size chart redraw parent instead of redrawing sub range table
		 * @param {Controller} ac
		 */
		postActionSizeChart: function(ac) {
			// For size chart we have a setting, which will enable to generate new Revision every time when use editing Product Group, Tolerance or Size Range of Size Chart
			// in this case we need to switch to new revision once editing is done
			var bNewRevision = _settingsNode.SCGenNewSizeChartRevision;
			if (bNewRevision) {
				var sizeChartNode = ac.m_response.getNodeOfType("SizeChart");
				var latestRevisionUrl = sizeChartNode.CurrentRevision; // Current REvision is "LatesRevision" for size charts
				session.setLocationUrl(latestRevisionUrl, true);
			} else {
				var w = ac.widget;
				// Check if action initiated from Subrange Table (this table inserted in and is part of Composite SizeChart's Property view)
				if (w.defView.m_id == "SubSizeRanges") {
					w = w.viewParent; // go to parent - Properties
				}

				//Properties view needs to refresh the data for the Avatar dropdown
				if (w.defView.m_id == "Properties") {
					w.postActionRefreshData(ac);
					_refreshDimensions(ac);
				}

				// Redraw only edited row for Table and whole widget for Property view
				w.postActionRedrawNode(ac);
			}
		},

		postActionSubSizeRange: function(ac) {
			this.postActionSizeChart(ac);
			_refreshDimensions(ac);
		},

		roundToFraction: function(/*Number*/inches) {
			var wholeInches = Math.round(inches);
			var frac = inches - wholeInches;
			var fraction = Math.round(frac * _roundToDenominator);
			return wholeInches + fraction / _roundToDenominator;
		},

		refreshPropertyViewAndData: function(ac) {
			// page.refreshViews here because when one tab is updated we want other tabs to be refreshed as well
			page.refreshViews();
			return ac.widget.refreshData();
		}
	};
});